import apiConfig from '../../config/apiConfig'
import { mapBrandCategoryResponse } from '../mappers/brandCategoryMapper'
import {
  mapDocumentTypeResponse,
  toDocumentTypeBody,
} from '../mappers/documentTypeMapper'
import {
  mapPromptModeResponse,
  mapPromptModeVersionResponse,
  toPromptModeBody,
  toPromptModeVersionBody,
} from '../mappers/promptModeMapper.js'
import { createBrandApiRequest } from '../utils/brandApiUtils'

export const createBrandService = ({ axiosInstance } = {}) => {
  const { apiKey = '', brandAPIs = {} } = apiConfig
  const {
    BRAND_CATEGORIES = '',
    BRANDS = '',
    DOCUMENT_TYPES = '',
    PROMPT_MODES = '',
    PROMPT_VERSIONS = '',
  } = brandAPIs
  const request = createBrandApiRequest({ apiKey, axiosInstance })

  return {
    createBrandCategory: async ({ active = true, code, name }) => {
      const response = await request({
        body: { active, code: code.trim(), name: name.trim() },
        endpoint: BRAND_CATEGORIES,
        method: 'post',
      })
      return mapBrandCategoryResponse([response])[0] || response
    },
    createDocumentType: async (documentType) => {
      const response = await request({
        body: toDocumentTypeBody(documentType),
        endpoint: DOCUMENT_TYPES,
        method: 'post',
      })
      return mapDocumentTypeResponse([response])[0] || response
    },
    getAllBrands: () => request({ endpoint: BRANDS }),
    getAllBrandCategories: async () => {
      const response = await request({ endpoint: BRAND_CATEGORIES })
      return mapBrandCategoryResponse(response)
    },
    getAllDocumentTypes: async () => {
      const response = await request({ endpoint: DOCUMENT_TYPES })
      return mapDocumentTypeResponse(response)
    },
    updateBrandCategory: async (id, { active, code, name }) => {
      const response = await request({
        body: { active, code: code.trim(), name: name.trim() },
        endpoint: `${BRAND_CATEGORIES}/${encodeURIComponent(id)}`,
        method: 'patch',
      })
      return mapBrandCategoryResponse([response])[0]
    },
    updateDocumentType: async (id, documentType) => {
      const response = await request({
        body: toDocumentTypeBody(documentType),
        endpoint: `${DOCUMENT_TYPES}/${encodeURIComponent(id)}`,
        method: 'patch',
      })
      return mapDocumentTypeResponse([response])[0]
    },
    getAllPromptModes: async () => {
      const response = await request({
        endpoint: PROMPT_MODES,
      })
      return mapPromptModeResponse(response)
    },
    getPromptModeVersions: async (modeCode) => {
      const response = await request({
        endpoint: `${PROMPT_MODES}/${encodeURIComponent(modeCode)}/versions`,
      })
      return mapPromptModeVersionResponse(response)
    },
    savePromptModeDraftVersion: async (
      modeCode,
      versionDraft,
      promptVersionId,
    ) => {
      const body = toPromptModeVersionBody(versionDraft)

      const response = await request(
        promptVersionId
          ? {
              body,
              endpoint: `${PROMPT_VERSIONS}/${encodeURIComponent(promptVersionId)}`,
              method: 'patch',
            }
          : {
              body,
              endpoint: `${PROMPT_MODES}/${encodeURIComponent(modeCode)}/versions`,
              method: 'post',
            },
      )

      return mapPromptModeVersionResponse([response])[0] || response
    },
    publishPromptModeVersion: async (promptVersionId) => {
      if (!promptVersionId) {
        throw new Error('Prompt version id is required to publish a version.')
      }

      const response = await request({
        endpoint: `${PROMPT_VERSIONS}/${encodeURIComponent(promptVersionId)}/publish`,
        method: 'post',
      })

      return mapPromptModeVersionResponse([response])[0] || response
    },
    rollbackPromptModeVersion: async (promptVersionId) => {
      if (!promptVersionId) {
        throw new Error('Prompt version id is required to rollback a version.')
      }

      const response = await request({
        endpoint: `${PROMPT_VERSIONS}/${encodeURIComponent(promptVersionId)}/rollback`,
        method: 'post',
      })

      return mapPromptModeVersionResponse([response])[0] || response
    },
    createPromptMode: async (promptMode) => {
      const response = await request({
        body: toPromptModeBody(promptMode),
        endpoint: PROMPT_MODES,
        method: 'post',
      })
      return mapPromptModeResponse(response)[0] || response
    },
    updatePromptMode: async (id, promptMode) => {
      const { code } = promptMode
      if (!code) {
        throw new Error('Prompt mode code is required for update.')
      }
      const response = await request({
        body: toPromptModeBody(promptMode),
        endpoint: `${PROMPT_MODES}/${encodeURIComponent(code)}`,
        method: 'patch',
      })
      return mapPromptModeResponse(response)[0] || response
    },
  }
}

export const brandService = createBrandService()
